<script setup>
import { ref, watch, computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputSwitch from 'primevue/inputswitch';
import Textarea from 'primevue/textarea';
import Calendar from 'primevue/calendar';
import axios from 'axios';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';

const props = defineProps({
  visible: { type: Boolean, required: true },
  date: { type: String, default: '' },
  type: { type: String, required: true }, // 'company' or 'provider'
  companyId: { type: [Number, String], required: true },
  providerId: { type: [Number, String], default: null },
  existing: { type: Object, default: null },
  defaultHours: { type: Object, default: null } // from weekly schedule
});

const emit = defineEmits(['update:visible', 'saved', 'cleared']);

const show = ref(props.visible);
const toast = useToast();
const confirm = useConfirm();
const loading = ref(false);

const form = ref({
  start_date: null,
  end_date: null,
  is_open: true,
  start_time: null,
  end_time: null,
  customer_start_time: null,
  customer_end_time: null,
  comment: ''
});

const parseTime = (timeStr) => {
  if (!timeStr) return null;
  if (timeStr instanceof Date) return timeStr;
  const parts = String(timeStr).split(':');
  if (parts.length < 2) return null;
  const date = new Date();
  date.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
  return date;
};

const formatTime = (date) => {
  if (!date) return null;
  if (!(date instanceof Date)) return String(date).substring(0, 5);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const formatDateToYMD = (date) => {
    if (!date) return null;
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
};

watch(() => props.visible, (v) => {
  show.value = v;
  if (v) {
    if (props.existing) {
      form.value.start_date = props.existing.start_date ? new Date(props.existing.start_date) : new Date(props.date);
      form.value.end_date = props.existing.end_date ? new Date(props.existing.end_date) : new Date(props.date);
      const isWorkday = (typeof props.existing.is_workday !== 'undefined') ? props.existing.is_workday : props.existing.is_open;
      form.value.is_open = !!isWorkday;
      form.value.start_time = parseTime(props.existing.start_time);
      form.value.end_time = parseTime(props.existing.end_time);
      form.value.customer_start_time = parseTime(props.existing.customer_start_time);
      form.value.customer_end_time = parseTime(props.existing.customer_end_time);
      form.value.comment = props.existing.reason ?? props.existing.comment ?? '';
    } else if (props.defaultHours) {
      form.value.start_date = new Date(props.date);
      form.value.end_date = new Date(props.date);
      form.value.is_open = !!props.defaultHours.is_workday;
      form.value.start_time = parseTime(props.defaultHours.start_time);
      form.value.end_time = parseTime(props.defaultHours.end_time);
      form.value.customer_start_time = parseTime(props.defaultHours.customer_start_time);
      form.value.customer_end_time = parseTime(props.defaultHours.customer_end_time);
      form.value.comment = '';
    } else {
      form.value.start_date = new Date(props.date);
      form.value.end_date = new Date(props.date);
      form.value.is_open = true;
      form.value.start_time = parseTime('08:00');
      form.value.end_time = parseTime('20:00');
      form.value.customer_start_time = null;
      form.value.customer_end_time = null;
      form.value.comment = '';
    }
  }
});

watch(show, (v) => emit('update:visible', v));

const save = async () => {
  loading.value = true;
  try {
    const endpoint = props.type === 'company' ? '/ajax/admin/company-special-days/upsert' : '/ajax/admin/provider-special-days/upsert';
    const isWorkday = !!form.value.is_open;
    const payload = {
      id: props.existing?.id,
      company_id: props.companyId,
      start_date: formatDateToYMD(form.value.start_date),
      end_date: formatDateToYMD(form.value.end_date) || formatDateToYMD(form.value.start_date),
      is_workday: isWorkday,
      reason: form.value.comment,
      start_time: formatTime(form.value.start_time),
      end_time: formatTime(form.value.end_time)
    };
    if (props.type === 'provider') {
      payload.provider_id = props.providerId;
      payload.customer_start_time = formatTime(form.value.customer_start_time);
      payload.customer_end_time = formatTime(form.value.customer_end_time);
    }

    await axios.post(endpoint, payload);
    toast.add({ severity: 'success', summary: 'Erfolg', detail: 'Sonderöffnungszeit gespeichert', life: 3000 });
    emit('saved');
    show.value = false;
  } catch (e) {
    const detail = e.response?.data?.errors ? Object.values(e.response.data.errors)[0][0] : (e.response?.data?.message || 'Speichern fehlgeschlagen');
    toast.add({ severity: 'error', summary: 'Fehler', detail: detail, life: 5000 });
  } finally {
    loading.value = false;
  }
};

const clear = async () => {
  if (!props.existing?.id) return;
  loading.value = true;
  try {
    const identifier = props.existing?.uuid || props.existing?.id;
    const endpoint = props.type === 'company' 
      ? `/ajax/admin/company-special-days/${identifier}` 
      : `/ajax/admin/provider-special-days/${identifier}`;

    await axios.delete(endpoint);
    toast.add({ severity: 'success', summary: 'Erfolgreich', detail: 'Sonderöffnungszeit entfernt', life: 3000 });
    emit('cleared');
    show.value = false;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Fehler', detail: 'Entfernen fehlgeschlagen', life: 3000 });
  } finally {
    loading.value = false;
  }
};

const confirmClear = () => {
  confirm.require({
    message: 'Sind Sie sicher, dass Sie diesen Eintrag entfernen möchten?',
    header: 'Löschen bestätigen',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Löschen',
    rejectLabel: 'Abbrechen',
    accept: () => {
      clear();
    }
  });
};
</script>
<template>
  <Dialog 
    v-model:visible="show" 
    modal 
    header="Tagesplan / Zeitraum bearbeiten" 
    :style="{ width: '450px' }"
  >
    <div class="space-y-6 pt-2">
      <!-- Date Display / Range -->
      <div class="flex items-end justify-between pb-4 border-b border-gray-100">
        <div class="flex items-end gap-2">
          <div class="flex flex-col gap-1">
            <label class="text-[10px] font-bold text-gray-500 uppercase">Startdatum</label>
            <Calendar v-model="form.start_date" dateFormat="dd.mm.yy" inputClass="w-28 text-sm p-2 font-bold border-gray-200" placeholder="Start" />
          </div>
          <span class="text-gray-400 font-bold mb-2">-</span>
          <div class="flex flex-col gap-1">
            <label class="text-[10px] font-bold text-gray-500 uppercase">Enddatum</label>
            <Calendar v-model="form.end_date" dateFormat="dd.mm.yy" inputClass="w-28 text-sm p-2 font-bold border-gray-200" placeholder="Ende" :minDate="form.start_date" />
          </div>
        </div>
        <div class="flex items-center gap-2 ml-2 mb-2">
          <InputSwitch v-model="form.is_open" />
          <span class="text-xs font-bold" :class="form.is_open ? 'text-green-600' : 'text-red-500'">
            {{ form.is_open ? 'GEÖFFNET' : 'GESCHLOSSEN' }}
          </span>
        </div>
      </div>

      <!-- Time Inputs -->
      <div v-if="form.is_open" class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-2">
          <label class="text-xs font-bold text-gray-500 uppercase">Beginn</label>
          <Calendar 
            v-model="form.start_time" 
            timeOnly 
            :stepMinute="15"
            hourFormat="24"
            placeholder="Beginn"
            showIcon
            iconDisplay="input"
            icon="pi pi-clock"
            inputClass="w-full text-sm font-medium border-gray-200 p-2"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-xs font-bold text-gray-500 uppercase">Ende</label>
          <Calendar 
            v-model="form.end_time" 
            timeOnly 
            :stepMinute="15"
            hourFormat="24"
            placeholder="Ende"
            showIcon
            iconDisplay="input"
            icon="pi pi-clock"
            inputClass="w-full text-sm font-medium border-gray-200 p-2"
          />
        </div>
      </div>

      <!-- Customer Time Inputs (Provider Only) -->
      <div v-if="form.is_open && type === 'provider'" class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-2">
          <label class="text-xs font-bold text-gray-500 uppercase">Kunden-Beginn</label>
          <Calendar 
            v-model="form.customer_start_time" 
            timeOnly 
            :stepMinute="15"
            hourFormat="24"
            placeholder="Kunden-Beginn"
            showIcon
            iconDisplay="input"
            icon="pi pi-clock"
            inputClass="w-full text-sm font-medium border-gray-200 p-2"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label class="text-xs font-bold text-gray-500 uppercase">Kunden-Ende</label>
          <Calendar 
            v-model="form.customer_end_time" 
            timeOnly 
            :stepMinute="15"
            hourFormat="24"
            placeholder="Kunden-Ende"
            showIcon
            iconDisplay="input"
            icon="pi pi-clock"
            inputClass="w-full text-sm font-medium border-gray-200 p-2"
          />
        </div>
      </div>

      <!-- Comment -->
      <div class="flex flex-col gap-2">
        <label class="text-xs font-bold text-gray-500 uppercase">Grund / Anmerkung</label>
        <Textarea 
          v-model="form.comment" 
          rows="2" 
          placeholder="Grund für die Ausnahme (optional)" 
          class="w-full text-sm"
        />
      </div>

      <!-- Actions -->
      <div class="pt-4 space-y-3">
        <div class="flex gap-3">
          <Button 
            @click="show = false" 
            label="ABBRECHEN" 
            text
            class="flex-1 !text-gray-500 font-bold py-3 rounded-xl border border-gray-100 hover:bg-gray-50" 
          />
          <Button 
            @click="save" 
            label="SPEICHERN" 
            :loading="loading"
            class="flex-1 !bg-black !text-[#FFF053] !border-none font-bold py-3 rounded-xl" 
          />
        </div>

        <Button 
          v-if="existing" 
          @click="confirmClear" 
          label="EINTRAG ENTFERNEN" 
          severity="danger"
          text
          class="w-full text-xs font-bold !text-red-500" 
        />
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
:deep(.p-inputswitch.p-inputswitch-checked .p-inputswitch-slider) {
    background: #000;
}
:deep(.p-calendar .p-inputtext) {
    border-radius: 8px;
}
:deep(.p-calendar:not(.p-calendar-disabled).p-focus > .p-inputtext) {
    border-color: #000;
    box-shadow: none;
}
</style>
